  // src/App.jsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import Home from './components/Home';
import Editor from './components/Editor';
import LoginPage from './components/LoginPage';
import ImageUploader from './components/ImageUploader';
import TemplateSelector from './components/TemplateSelector';
import CustomizationPage from './components/CustomizationPage';
import RegistrationPage from './components/RegistrationPage';
import UserDashboard from './components/UserDashboard';
import NewDashboard from './components/NewDashboard';
import { UserSignupForm } from './components/UserSignupForm';
import { EventForm } from './components/EventForm';
import { EventTemplates } from './components/EventTemplates';
import EventDashboard from './components/EventDashboard';
import UserImagesUpload from './components/UserImagesUpload';
import CRMDashboard from './components/crm/CRMDashboard';
import AdminStaffCreator from './components/AdminStaffCreator';
import UserManagement from './components/UserManagement';
import supabase from './utils/supabaseUtils';
import ImageApprovalPage from './components/ImageApprovalPage';
import Layout from './components/Layout';
import About from './components/About';
import Contact from './components/Contact';
import Pricing from './components/Pricing';
import './styles/theme.css';

// Alias the dashboard components for clarity
const OldDashboardComponent = UserDashboard;
const MainDashboardComponent = NewDashboard;

// Create a custom theme with our 2025 minimal design style
const theme = extendTheme({
  colors: {
    brand: {
      // Primary brand color (warm orange)
      500: '#D97C2B',
      600: '#C16A20', // darker for hover states
    },
    accent: {
      // Secondary brand color (coastal green)
      500: '#5A9A7E',
      600: '#4B8C70', // darker for hover states
    },
    text: {
      primary: '#2A2A2A', // Rich Gray
      secondary: '#696969', // Medium Gray for secondary text
      light: '#8A8A8A', // Light Gray for tertiary text
    },
    neutral: {
      100: '#FDFDFC', // Lightest background
      200: '#F7F7F7', // Light background
      300: '#EEEEEE', // Borders
      400: '#C3C3C3', // Soft Neutral
    },
    bg: {
      primary: '#FDFDFC',
      secondary: '#F7F7F7',
    }
  },
  fonts: {
    body: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
    heading: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: "300",
        borderRadius: "8px",
        letterSpacing: "0.01em",
      },
      variants: {
        solid: {
          bg: "brand.500",
          color: "white",
          _hover: {
            bg: "brand.600",
            boxShadow: "0 4px 12px rgba(217, 124, 43, 0.2)",
            transform: "translateY(-2px)",
          },
        },
        secondary: {
          bg: "accent.500",
          color: "white",
          _hover: {
            bg: "accent.600",
            boxShadow: "0 4px 12px rgba(90, 154, 126, 0.2)",
            transform: "translateY(-2px)",
          },
        },
        outline: {
          borderColor: "brand.500",
          color: "brand.500",
          borderWidth: "1px",
          _hover: {
            bg: "rgba(217, 124, 43, 0.05)",
            borderColor: "brand.600",
          },
        },
        ghost: {
          color: "text.primary",
          _hover: {
            bg: "neutral.200",
          },
        },
        neutral: {
          bg: "neutral.400",
          color: "text.primary",
          _hover: {
            bg: "#B1B1B1",
            transform: "translateY(-2px)",
          },
        }
      },
    },
    Input: {
      baseStyle: {
        field: {
          borderRadius: "8px",
          fontWeight: "300",
        },
      },
      variants: {
        outline: {
          field: {
            borderColor: "rgba(0,0,0,0.08)",
            _focus: {
              borderColor: "brand.500",
              boxShadow: "0 0 0 3px rgba(217, 124, 43, 0.1)",
            },
          },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: "10px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          bg: "white",
          border: "1px solid rgba(0,0,0,0.03)",
          transition: "all 0.2s ease-in-out",
          _hover: {
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            transform: "translateY(-3px)",
          }
        },
      },
    },
    Heading: {
      baseStyle: {
        color: "text.primary",
        fontWeight: "300",
        letterSpacing: "-0.01em",
      },
    },
    Text: {
      baseStyle: {
        color: "text.primary",
        fontWeight: "300",
      },
    },
  },
  styles: {
    global: {
      body: {
        bg: "bg.primary",
        color: "text.primary",
        fontFamily: "'Outfit', sans-serif",
        fontWeight: "300",
      },
    },
  },
});

// Import debug utils in development mode only
const importDebugUtils = async () => {
  if (import.meta.env.DEV) {
    try {
      await import('./debug/debugApp');
      console.log('Debug utilities loaded in development mode');
    } catch (error) {
      console.error('Failed to load debug utilities:', error);
    }
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Load debug utils on component mount
  useEffect(() => {
    importDebugUtils();
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (data.session) {
        setIsAuthenticated(true);
        const { user } = data.session;
        setUserInfo({
          firstName: user.user_metadata.given_name || '',
          lastName: user.user_metadata.family_name || '',
          email: user.email || '',
        });
        setSession(data.session);
        
        // Log the user login
        supabase.auth.getUser().then(({ data }) => {
          if (data && data.user) {
            // Log user login via direct function call
            fetch(`https://api.ipify.org?format=json`)
              .then(res => res.json())
              .then(ipData => {
                supabase.rpc('log_user_login', { 
                  p_user_id: data.user.id,
                  p_ip_address: ipData.ip || 'unknown',
                  p_user_agent: navigator.userAgent || 'unknown'
                })
                .then(result => {
                  // Successfully logged login
                  console.log('Login logged successfully in checkSession');
                })
                .catch(err => {
                  console.warn('Failed to log user login:', err);
                });
              })
              .catch(() => {
                // If getting IP fails, still try to log with unknown IP
                supabase.rpc('log_user_login', { 
                  p_user_id: data.user.id,
                  p_ip_address: 'unknown',
                  p_user_agent: navigator.userAgent || 'unknown'
                })
                .then(result => {
                  // Successfully logged login
                  console.log('Login logged successfully in checkSession with unknown IP');
                })
                .catch(err => {
                  console.warn('Failed to log user login:', err);
                });
              });
          }
        }).catch(err => {
          console.warn('Failed to get user for login log:', err);
        });
      }
      setLoading(false);
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setIsAuthenticated(true);
        setUserInfo({
          firstName: session.user.user_metadata.given_name || '',
          lastName: session.user.user_metadata.family_name || '',
          email: session.user.email || '',
        });
        setSession(session);
        
        // Log login on sign in event
        if (event === 'SIGNED_IN') {
          fetch(`https://api.ipify.org?format=json`)
            .then(res => res.json())
            .then(ipData => {
              supabase.rpc('log_user_login', { 
                p_user_id: session.user.id,
                p_ip_address: ipData.ip || 'unknown',
                p_user_agent: navigator.userAgent || 'unknown'
              })
              .then(result => {
                // Successfully logged login
                console.log('Login logged successfully');
              })
              .catch(err => {
                console.warn('Failed to log user login on sign in:', err);
              });
            })
            .catch(() => {
              // If getting IP fails, still try to log with unknown IP
              supabase.rpc('log_user_login', { 
                p_user_id: session.user.id,
                p_ip_address: 'unknown',
                p_user_agent: navigator.userAgent || 'unknown'
              })
              .then(result => {
                // Successfully logged login
                console.log('Login logged successfully with unknown IP');
              })
              .catch(err => {
                console.warn('Failed to log user login on sign in:', err);
              });
            });
        }
      } else {
        setIsAuthenticated(false);
        setUserInfo(null);
        setSession(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  if (loading) return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#FDFDFC',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: '3px solid #EEEEEE',
        borderTopColor: '#D97C2B',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      <p style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: '16px',
        fontWeight: '500',
        color: '#2A2A2A',
        margin: 0
      }}>Loading...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return (
    <ChakraProvider theme={theme}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home isAuthenticated={isAuthenticated} userInfo={userInfo} />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/signup" element={<UserSignupForm />} />
          <Route path="/signup/:eventId" element={<UserSignupForm />} />
          <Route path="/approve/:approvalId" element={<ImageApprovalPage />} />
          
          {/* Protected routes with Layout */}
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
            <Route path="/upload" element={
              <Layout>
                <ImageUploader onUploadComplete={(url) => console.log(url)} />
              </Layout>
            } />
            <Route path="/templates" element={
              <Layout>
                <TemplateSelector />
              </Layout>
            } />
            <Route path="/editor" element={
              <Layout>
                <Editor />
              </Layout>
            } />
            <Route path="/customize/:templateId" element={
              <Layout>
                <CustomizationPage />
              </Layout>
            } />
            <Route path="/old-dashboard" element={
              <Layout>
                <OldDashboardComponent />
              </Layout>
            } />
            <Route path="/dashboard" element={
              <Layout>
                <MainDashboardComponent />
              </Layout>
            } />
            <Route path="/dashboard/events/new" element={
              <Layout>
                <EventForm />
              </Layout>
            } />
            <Route path="/dashboard/events/:eventId/edit" element={
              <Layout>
                <EventForm />
              </Layout>
            } />
            <Route path="/events/:eventId/templates" element={
              <Layout>
                <EventTemplates />
              </Layout>
            } />
            <Route path="/dashboard/events/:eventId" element={
              <Layout>
                <EventDashboard />
              </Layout>
            } />
            <Route path="/dashboard/events/:eventId/users/:userId/images" element={
              <Layout>
                <UserImagesUpload />
              </Layout>
            } />
            <Route path="/dashboard/crm" element={
              <Layout>
                <CRMDashboard />
              </Layout>
            } />
            <Route path="/dashboard/staff/new" element={
              <Layout>
                <AdminStaffCreator />
              </Layout>
            } />
            <Route path="/dashboard/users" element={
              <Layout>
                <UserManagement />
              </Layout>
            } />
          </Route>
          
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </ChakraProvider>
  );
}

// Add this component for protected route handling
const ProtectedRoute = ({ isAuthenticated }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  return <Outlet />;
};

export default App;